/**
 * ==========================================================================
 *  personalisierung.js — Text-Bausteine fuer die Sicht der Beteiligten
 * ==========================================================================
 *  Liefert die 5 Stufen pro Perspektive (pflegebeduerftige Person /
 *  pflegende Angehoerige) als personalisierte Texte. Verwandtschaft,
 *  Geschlecht und Vornamen aus den Kunden-Stammdaten werden automatisch
 *  in die Vorlagen eingesetzt.
 *
 *  Beispiel-Konstellation (Tochter Maria pflegt Mutter Anna):
 *    Patient-Sicht Lvl 2:
 *      "...Meine Tochter Maria unterstuetzt mich verlaesslich,..."
 *    Pflegeperson-Sicht Lvl 3:
 *      "Die Pflege meiner Mutter Anna kostet mich immer mehr Kraft..."
 *
 *  Bei fehlenden Stammdaten werden generische Fallback-Begriffe
 *  ("meine Pflegeperson") eingesetzt — die Buttons bleiben nutzbar.
 * ==========================================================================
 */

// --------------------------------------------------------------------------
// Lookup: Verwandtschafts-Bezeichnungen aus Patient-Sicht (so wie er/sie
// ueber den Angehoerigen spricht). Pro Verwandtschaftstyp drei Faelle:
//   nom = Nominativ ("meine Tochter ...")
//   akk = Akkusativ ("meine Tochter ..." / "meinen Sohn ...")
//   dat = Dativ     ("meiner Tochter ..." / "meinem Sohn ...")
// --------------------------------------------------------------------------
const VERWANDT_AUS_PATIENT_SICHT = {
  Tochter:           { nom: 'meine Tochter',           akk: 'meine Tochter',           dat: 'meiner Tochter' },
  Sohn:              { nom: 'mein Sohn',               akk: 'meinen Sohn',             dat: 'meinem Sohn' },
  Ehefrau:           { nom: 'meine Ehefrau',           akk: 'meine Ehefrau',           dat: 'meiner Ehefrau' },
  Ehemann:           { nom: 'mein Ehemann',            akk: 'meinen Ehemann',          dat: 'meinem Ehemann' },
  Lebensgefährtin:   { nom: 'meine Lebensgefährtin',   akk: 'meine Lebensgefährtin',   dat: 'meiner Lebensgefährtin' },
  Lebensgefährte:    { nom: 'mein Lebensgefährte',     akk: 'meinen Lebensgefährten',  dat: 'meinem Lebensgefährten' },
  Mutter:            { nom: 'meine Mutter',            akk: 'meine Mutter',            dat: 'meiner Mutter' },
  Vater:             { nom: 'mein Vater',              akk: 'meinen Vater',            dat: 'meinem Vater' },
  Schwester:         { nom: 'meine Schwester',         akk: 'meine Schwester',         dat: 'meiner Schwester' },
  Bruder:            { nom: 'mein Bruder',             akk: 'meinen Bruder',           dat: 'meinem Bruder' },
  Schwiegertochter:  { nom: 'meine Schwiegertochter',  akk: 'meine Schwiegertochter',  dat: 'meiner Schwiegertochter' },
  Schwiegersohn:     { nom: 'mein Schwiegersohn',      akk: 'meinen Schwiegersohn',    dat: 'meinem Schwiegersohn' },
  // Enkel ist im UI als gemischter Eintrag "Enkel/Enkelin" ausgewaehlt;
  // Geschlecht entscheidet ueber Form. Default = maskulin.
  Enkel:             { nom: 'mein Enkel',              akk: 'meinen Enkel',            dat: 'meinem Enkel' },
  Enkelin:           { nom: 'meine Enkelin',           akk: 'meine Enkelin',           dat: 'meiner Enkelin' },
  Nachbarin:         { nom: 'meine Nachbarin',         akk: 'meine Nachbarin',         dat: 'meiner Nachbarin' },
  Nachbar:           { nom: 'mein Nachbar',            akk: 'meinen Nachbarn',         dat: 'meinem Nachbarn' },
  Freundin:          { nom: 'meine Freundin',          akk: 'meine Freundin',          dat: 'meiner Freundin' },
  Freund:            { nom: 'mein Freund',             akk: 'meinen Freund',           dat: 'meinem Freund' },
  Sonstiges:         { nom: 'meine Pflegeperson',      akk: 'meine Pflegeperson',      dat: 'meiner Pflegeperson' },
};

// --------------------------------------------------------------------------
// Kind-Modus: spezielle Bezeichnungen + Texte fuer pflegebeduerftige Personen
// unter 12 Jahren. Eltern heissen "Mama" / "Papa" (ohne Possessivpronomen),
// alle anderen Verwandtschaften bleiben in der Erwachsenen-Form ("meine
// Tante Petra"). Texte sind in einfacherer, kindgerechter Sprache verfasst.
// --------------------------------------------------------------------------
const VERWANDT_AUS_KIND_SICHT_OVERRIDES = {
  Mutter: { nom: 'Mama', akk: 'Mama', dat: 'Mama' },
  Vater:  { nom: 'Papa', akk: 'Papa', dat: 'Papa' },
};


function _alterAusGeburtsdatum(dobIso) {
  if (!dobIso) return null;
  // dobIso ist 'YYYY-MM-DD' aus dem Backend.
  const teile = dobIso.split('-');
  if (teile.length !== 3) return null;
  const geburt = new Date(parseInt(teile[0]), parseInt(teile[1]) - 1, parseInt(teile[2]));
  if (isNaN(geburt.getTime())) return null;
  const heute = new Date();
  let alter = heute.getFullYear() - geburt.getFullYear();
  const m = heute.getMonth() - geburt.getMonth();
  if (m < 0 || (m === 0 && heute.getDate() < geburt.getDate())) alter -= 1;
  return alter;
}


export function istKind(kunde) {
  // Pflegebeduerftige Person ist ein Kind, wenn unter 12 Jahre alt.
  // Damit triggert der Kind-Modus automatisch im Sicht-Bausteine-Block.
  const alter = _alterAusGeburtsdatum(kunde?.dob);
  return alter !== null && alter < 12;
}


// --------------------------------------------------------------------------
// Lookup: invers — wie der Angehoerige ueber den Patienten spricht.
// Haengt vom Verwandtschaftstyp UND vom Patient-Geschlecht ab.
// --------------------------------------------------------------------------
const PATIENT_AUS_PFLEGEPERSON_SICHT = {
  // Tochter / Sohn pflegen Mutter (weiblich) oder Vater (maennlich)
  Tochter: {
    weiblich: { nom: 'meine Mutter',  akk: 'meine Mutter',   dat: 'meiner Mutter' },
    maennlich:{ nom: 'mein Vater',    akk: 'meinen Vater',   dat: 'meinem Vater' },
  },
  Sohn: {
    weiblich: { nom: 'meine Mutter',  akk: 'meine Mutter',   dat: 'meiner Mutter' },
    maennlich:{ nom: 'mein Vater',    akk: 'meinen Vater',   dat: 'meinem Vater' },
  },
  // Ehefrau pflegt Ehemann, Ehemann pflegt Ehefrau
  Ehefrau: {
    weiblich: { nom: 'meine Ehefrau', akk: 'meine Ehefrau',  dat: 'meiner Ehefrau' },
    maennlich:{ nom: 'mein Ehemann',  akk: 'meinen Ehemann', dat: 'meinem Ehemann' },
  },
  Ehemann: {
    weiblich: { nom: 'meine Ehefrau', akk: 'meine Ehefrau',  dat: 'meiner Ehefrau' },
    maennlich:{ nom: 'mein Ehemann',  akk: 'meinen Ehemann', dat: 'meinem Ehemann' },
  },
  Lebensgefährtin: {
    weiblich: { nom: 'meine Lebensgefährtin', akk: 'meine Lebensgefährtin', dat: 'meiner Lebensgefährtin' },
    maennlich:{ nom: 'mein Lebensgefährte',   akk: 'meinen Lebensgefährten', dat: 'meinem Lebensgefährten' },
  },
  Lebensgefährte: {
    weiblich: { nom: 'meine Lebensgefährtin', akk: 'meine Lebensgefährtin', dat: 'meiner Lebensgefährtin' },
    maennlich:{ nom: 'mein Lebensgefährte',   akk: 'meinen Lebensgefährten', dat: 'meinem Lebensgefährten' },
  },
  // Mutter / Vater pflegen erwachsene Tochter (weiblich) oder Sohn (maennlich)
  Mutter: {
    weiblich: { nom: 'meine Tochter', akk: 'meine Tochter', dat: 'meiner Tochter' },
    maennlich:{ nom: 'mein Sohn',     akk: 'meinen Sohn',   dat: 'meinem Sohn' },
  },
  Vater: {
    weiblich: { nom: 'meine Tochter', akk: 'meine Tochter', dat: 'meiner Tochter' },
    maennlich:{ nom: 'mein Sohn',     akk: 'meinen Sohn',   dat: 'meinem Sohn' },
  },
  Schwester: {
    weiblich: { nom: 'meine Schwester', akk: 'meine Schwester', dat: 'meiner Schwester' },
    maennlich:{ nom: 'mein Bruder',     akk: 'meinen Bruder',   dat: 'meinem Bruder' },
  },
  Bruder: {
    weiblich: { nom: 'meine Schwester', akk: 'meine Schwester', dat: 'meiner Schwester' },
    maennlich:{ nom: 'mein Bruder',     akk: 'meinen Bruder',   dat: 'meinem Bruder' },
  },
  // Schwiegertochter / Schwiegersohn pflegen Schwiegereltern
  Schwiegertochter: {
    weiblich: { nom: 'meine Schwiegermutter', akk: 'meine Schwiegermutter', dat: 'meiner Schwiegermutter' },
    maennlich:{ nom: 'mein Schwiegervater',   akk: 'meinen Schwiegervater', dat: 'meinem Schwiegervater' },
  },
  Schwiegersohn: {
    weiblich: { nom: 'meine Schwiegermutter', akk: 'meine Schwiegermutter', dat: 'meiner Schwiegermutter' },
    maennlich:{ nom: 'mein Schwiegervater',   akk: 'meinen Schwiegervater', dat: 'meinem Schwiegervater' },
  },
  // Enkel pflegt Grosseltern
  Enkel: {
    weiblich: { nom: 'meine Großmutter', akk: 'meine Großmutter', dat: 'meiner Großmutter' },
    maennlich:{ nom: 'mein Großvater',   akk: 'meinen Großvater', dat: 'meinem Großvater' },
  },
  Enkelin: {
    weiblich: { nom: 'meine Großmutter', akk: 'meine Großmutter', dat: 'meiner Großmutter' },
    maennlich:{ nom: 'mein Großvater',   akk: 'meinen Großvater', dat: 'meinem Großvater' },
  },
  // Nachbarn / Freunde sprechen ueber Patient als Nachbar/in oder Freund/in
  Nachbarin: {
    weiblich: { nom: 'meine Nachbarin', akk: 'meine Nachbarin', dat: 'meiner Nachbarin' },
    maennlich:{ nom: 'mein Nachbar',    akk: 'meinen Nachbarn', dat: 'meinem Nachbarn' },
  },
  Nachbar: {
    weiblich: { nom: 'meine Nachbarin', akk: 'meine Nachbarin', dat: 'meiner Nachbarin' },
    maennlich:{ nom: 'mein Nachbar',    akk: 'meinen Nachbarn', dat: 'meinem Nachbarn' },
  },
  Freundin: {
    weiblich: { nom: 'meine Freundin', akk: 'meine Freundin', dat: 'meiner Freundin' },
    maennlich:{ nom: 'mein Freund',    akk: 'meinen Freund',  dat: 'meinem Freund' },
  },
  Freund: {
    weiblich: { nom: 'meine Freundin', akk: 'meine Freundin', dat: 'meiner Freundin' },
    maennlich:{ nom: 'mein Freund',    akk: 'meinen Freund',  dat: 'meinem Freund' },
  },
  Sonstiges: {
    weiblich: { nom: 'die zu pflegende Person', akk: 'die zu pflegende Person', dat: 'der zu pflegenden Person' },
    maennlich:{ nom: 'die zu pflegende Person', akk: 'die zu pflegende Person', dat: 'der zu pflegenden Person' },
  },
};

// --------------------------------------------------------------------------
// Geschlechts-Inferenz aus salutation/Anrede oder Verwandtschaft, falls
// nichts gesetzt ist. Verwandtschaftstypen wie "Tochter"/"Sohn" sind
// geschlechts-eindeutig; nur bei "Sonstiges"/Enkel ist es offen.
// --------------------------------------------------------------------------
function geschlechtAusAnrede(salutation) {
  if (!salutation) return null;
  const s = salutation.trim().toLowerCase();
  if (s === 'frau') return 'weiblich';
  if (s === 'herr') return 'maennlich';
  return null; // 'Divers' oder Unbekannt
}

function geschlechtAusVerwandtschaft(rel) {
  const tabelle = {
    // weiblich
    Tochter: 'weiblich', Ehefrau: 'weiblich', Lebensgefährtin: 'weiblich',
    Mutter: 'weiblich', Schwester: 'weiblich', Schwiegertochter: 'weiblich',
    Enkelin: 'weiblich', Nachbarin: 'weiblich', Freundin: 'weiblich',
    // maennlich
    Sohn: 'maennlich', Ehemann: 'maennlich', Lebensgefährte: 'maennlich',
    Vater: 'maennlich', Bruder: 'maennlich', Schwiegersohn: 'maennlich',
    Enkel: 'maennlich', Nachbar: 'maennlich', Freund: 'maennlich',
  };
  return tabelle[rel] || null;
}

// --------------------------------------------------------------------------
// Helfer: Bezeichnung mit optionalem Vornamen zusammenbauen.
//   bezeichnung = "meine Tochter", vorname = "Maria"  ->  "meine Tochter Maria"
//   bezeichnung = "meine Pflegeperson", vorname = ""  ->  "meine Pflegeperson"
// --------------------------------------------------------------------------
function mitName(bezeichnung, vorname) {
  if (!vorname || !vorname.trim()) return bezeichnung;
  return `${bezeichnung} ${vorname.trim()}`;
}

// --------------------------------------------------------------------------
// Hauptfunktion: liefert die 4 personalisierten Bausteine fuer einen
// gegebenen Kunden (+ Angehoerigen).
//
//   kunde       = selectedClient (firstName, lastName, salutation, relative)
//   angehoeriger= kunde.relative (firstName, lastName, relationship, salutation)
//
//   Rueckgabe = {
//     ang_nom, ang_akk, ang_dat,    // wie Patient ueber Angehoerigen spricht
//     pat_nom_pp, pat_akk_pp, pat_dat_pp,  // wie Pflegeperson ueber Patient spricht
//     hat_angehoerigen_daten: bool, // false -> alle Werte sind Fallbacks
//   }
// --------------------------------------------------------------------------
export function bauePlatzhalter(kunde, modus = 'erwachsen') {
  const ang = kunde?.relative;
  const hat = !!(ang && (ang.firstName || ang.relationship));

  const verwandt = (ang?.relationship || 'Sonstiges').trim() || 'Sonstiges';
  const angVorname = ang?.firstName || '';

  // Patient -> Angehoeriger
  // Im Kind-Modus ueberschreiben wir nur Mama/Papa; alles andere bleibt
  // in der Erwachsenen-Form ("meine Tante Petra" — auch ein Kind sagt das so).
  const angTabelle =
    (modus === 'kind' && VERWANDT_AUS_KIND_SICHT_OVERRIDES[verwandt])
      || VERWANDT_AUS_PATIENT_SICHT[verwandt]
      || VERWANDT_AUS_PATIENT_SICHT.Sonstiges;

  // Angehoeriger -> Patient (braucht Patient-Geschlecht)
  const patGeschlecht =
    geschlechtAusAnrede(kunde?.salutation) ||
    'weiblich'; // Fallback wenn Anrede fehlt — neutral genug fuer "meine Mutter"
  const patTabelleZweig = PATIENT_AUS_PFLEGEPERSON_SICHT[verwandt] || PATIENT_AUS_PFLEGEPERSON_SICHT.Sonstiges;
  const patTabelle = patTabelleZweig[patGeschlecht] || patTabelleZweig.weiblich;
  const patVorname = kunde?.firstName || '';

  // Bei generischen Bezeichnungen ("Sonstiges" / "die zu pflegende Person")
  // UND im Kind-Modus bei Mama/Papa wuerde der angehaengte Vorname klobig
  // wirken — also weglassen.
  const istSonstiges = verwandt === 'Sonstiges';
  const istKindMamaPapa = modus === 'kind' && (verwandt === 'Mutter' || verwandt === 'Vater');
  const angNameOpt  = (istSonstiges || istKindMamaPapa) ? '' : angVorname;
  const patNameOpt  = istSonstiges ? '' : patVorname;

  return {
    ang_nom: mitName(angTabelle.nom, angNameOpt),
    ang_akk: mitName(angTabelle.akk, angNameOpt),
    ang_dat: mitName(angTabelle.dat, angNameOpt),
    pat_nom_pp: mitName(patTabelle.nom, patNameOpt),
    pat_akk_pp: mitName(patTabelle.akk, patNameOpt),
    pat_dat_pp: mitName(patTabelle.dat, patNameOpt),
    hat_angehoerigen_daten: hat,
  };
}

// --------------------------------------------------------------------------
// Templates fuer beide Perspektiven, je 5 Stufen.
// Platzhalter:
//   {ang_nom} {ang_akk} {ang_dat}   = wie Patient ueber Angehoerigen spricht
//   {pat_nom_pp} {pat_akk_pp} {pat_dat_pp} = wie Pflegeperson ueber Patient spricht
// --------------------------------------------------------------------------
export const STUFEN_PATIENT = [
  {
    label: 'Sehr zufrieden',
    farbe: '#16A34A',  // gruen-600
    text:
      'Ich fühle mich zu Hause sicher, gut aufgehoben und mit meiner derzeitigen ' +
      'Pflegesituation sehr zufrieden. Ich vertraue {ang_dat} und habe das Gefühl, ' +
      'dass meine notwendige Unterstützung zuverlässig funktioniert.',
  },
  {
    label: 'Gut zurecht',
    farbe: '#65A30D',  // lime-600
    text:
      'Insgesamt komme ich mit der aktuellen Pflegesituation gut zurecht, auch ' +
      'wenn es gelegentlich anstrengende oder unsichere Momente gibt. {ang_nom} ' +
      'unterstützt mich verlässlich, aber einzelne Bereiche könnten besser ' +
      'organisiert oder entlastet werden.',
  },
  {
    label: 'Mehr Hilfe',
    farbe: '#CA8A04',  // yellow-600
    text:
      'Ich merke, dass meine Selbstständigkeit nachlässt und ich häufiger Hilfe ' +
      'brauche als früher. Die Versorgung zu Hause durch {ang_akk} gelingt noch, ' +
      'ist aber für uns beide zunehmend anstrengend.',
  },
  {
    label: 'Stark belastet',
    farbe: '#EA580C',  // orange-600
    text:
      'Meine gesundheitliche Situation hat sich spürbar verschlechtert und ich ' +
      'brauche in vielen Bereichen regelmäßige Unterstützung. Ich habe Sorge, ' +
      'dass die aktuelle Versorgung auf Dauer nicht mehr ausreicht oder {ang_nom} ' +
      'überlastet wird.',
  },
  {
    label: 'Kritische Situation',
    farbe: '#DC2626',  // red-600
    text:
      'Mir geht es körperlich, seelisch oder organisatorisch sehr schlecht und ' +
      'ich bin in hohem Maß auf Hilfe angewiesen. Die häusliche Versorgung ist ' +
      'stark belastet oder gefährdet, weil mein Hilfebedarf deutlich gestiegen ' +
      'ist und die bisherigen Möglichkeiten kaum noch ausreichen.',
  },
];

export const STUFEN_PFLEGEPERSON = [
  {
    label: 'Gut machbar',
    farbe: '#16A34A',
    text:
      'Ich erlebe die Pflege von {pat_dat_pp} derzeit als gut organisiert und ' +
      'insgesamt gut zu bewältigen. Die Unterstützung passt zum Hilfebedarf, ' +
      'und ich fühle mich meiner Aufgabe gewachsen.',
  },
  {
    label: 'Meist stabil',
    farbe: '#65A30D',
    text:
      'Die Pflege von {pat_dat_pp} ist im Alltag überwiegend gut leistbar, auch ' +
      'wenn es einzelne belastende Situationen gibt. Ich komme grundsätzlich ' +
      'zurecht, wünsche mir aber punktuell mehr Unterstützung oder Entlastung.',
  },
  {
    label: 'Mehr Entlastung',
    farbe: '#CA8A04',
    text:
      'Der Unterstützungsbedarf von {pat_dat_pp} nimmt spürbar zu, und die Pflege ' +
      'kostet mich immer mehr Kraft, Zeit und Aufmerksamkeit. Die Versorgung ' +
      'gelingt noch, aber ich merke deutliche Belastungsgrenzen.',
  },
  {
    label: 'Stark belastet',
    farbe: '#EA580C',
    text:
      'Die Pflege von {pat_dat_pp} ist für mich inzwischen deutlich belastend ' +
      'und nur noch mit erheblichem Aufwand zu bewältigen. Ich mache mir Sorgen, ' +
      'ob ich die Versorgung dauerhaft in dieser Form aufrechterhalten kann.',
  },
  {
    label: 'Kritisch / überfordert',
    farbe: '#DC2626',
    text:
      'Ich fühle mich mit der Pflege von {pat_dat_pp} stark überfordert oder ' +
      'am Ende meiner Kräfte. Die häusliche Versorgung ist aus meiner Sicht ' +
      'gefährdet, wenn nicht kurzfristig zusätzliche Hilfe, Entlastung oder ' +
      'eine neue Lösung organisiert wird.',
  },
];

// --------------------------------------------------------------------------
// Templates fuer Kinder unter 12: einfache, kindgerechte Sprache. Wird
// automatisch aktiv, wenn das Geburtsdatum des Kunden ein Alter < 12
// ergibt (siehe istKind). Wenn das Kind sich nicht selbst aeussern kann
// (Saeuglinge, schwere Beeintraechtigung), greift weiter STUFEN_PATIENT_BEOBACHTET.
//
// Im Kind-Modus werden Mutter/Vater zu "Mama"/"Papa" (ohne Vorname),
// alle anderen Verwandtschaften behalten ihre normale Form.
// --------------------------------------------------------------------------
export const STUFEN_PATIENT_KIND = [
  {
    label: 'Sehr zufrieden',
    farbe: '#16A34A',
    text:
      'Zuhause fühl ich mich gut. {ang_nom} kümmert sich super um mich, ' +
      'und ich bin gerne hier.',
  },
  {
    label: 'Gut zurecht',
    farbe: '#65A30D',
    text:
      'Zuhause ist meistens okay. {ang_nom} kümmert sich gut um mich, auch ' +
      'wenn es manchmal anstrengend wird.',
  },
  {
    label: 'Mehr Hilfe',
    farbe: '#CA8A04',
    text:
      'Ich brauche immer mehr Hilfe als früher. {ang_nom} hilft mir viel, ' +
      'aber wir merken beide, dass es nicht einfach ist.',
  },
  {
    label: 'Stark belastet',
    farbe: '#EA580C',
    text:
      'Es geht mir oft nicht so gut, und ich kann vieles nicht mehr alleine. ' +
      '{ang_nom} hat ganz schön viel zu tun, und ich mache mir Sorgen.',
  },
  {
    label: 'Kritische Situation',
    farbe: '#DC2626',
    text:
      'Mir geht\'s gerade richtig schlecht. Ich kann fast nichts mehr alleine. ' +
      '{ang_nom} schafft das alles fast nicht mehr — ich glaube, wir brauchen ' +
      'mehr Hilfe.',
  },
];


// --------------------------------------------------------------------------
// Templates fuer den Fall "Patient kann sich selber NICHT aeussern"
// (Demenz, schwere Sprachstoerung, Saeuglinge/Kleinkinder, Schlaganfall...).
// In diesem Fall spricht der Angehoerige im Namen / aus seiner Wahrnehmung
// ueber den Patienten — nicht der Patient in Ich-Form.
//
// Platzhalter:
//   {pat_nom_pp} {pat_akk_pp}  = wie der Angehoerige ueber den Patienten
//                                spricht (z.B. "mein Sohn Paul")
// --------------------------------------------------------------------------
export const STUFEN_PATIENT_BEOBACHTET = [
  {
    label: 'Sehr zufrieden',
    farbe: '#16A34A',
    text:
      '{pat_nom_pp} wirkt zu Hause sicher und gut aufgehoben. Soweit wir es ' +
      'einschätzen können, scheint die aktuelle Pflegesituation als angenehm ' +
      'erlebt zu werden — wir sehen Vertrauen, Ruhe und Wohlbefinden.',
  },
  {
    label: 'Gut zurecht',
    farbe: '#65A30D',
    text:
      'Insgesamt scheint {pat_nom_pp} mit der Pflegesituation überwiegend gut ' +
      'zurechtzukommen, auch wenn es gelegentlich anstrengende oder unsichere ' +
      'Momente gibt. Wir nehmen wahr, dass die Versorgung verlässlich greift.',
  },
  {
    label: 'Mehr Hilfe',
    farbe: '#CA8A04',
    text:
      'Wir beobachten, dass {pat_nom_pp} an Selbstständigkeit verliert und ' +
      'häufiger Hilfe braucht als früher. Die Versorgung zu Hause gelingt noch, ' +
      'ist aber für uns alle zunehmend anstrengend.',
  },
  {
    label: 'Stark belastet',
    farbe: '#EA580C',
    text:
      'Die gesundheitliche Situation von {pat_dat_pp} hat sich spürbar ' +
      'verschlechtert. Wir machen uns Sorgen, dass die aktuelle Versorgung auf ' +
      'Dauer nicht mehr ausreicht oder die Pflegeperson überlastet wird.',
  },
  {
    label: 'Kritische Situation',
    farbe: '#DC2626',
    text:
      'Wir erleben {pat_akk_pp} körperlich, seelisch oder organisatorisch in ' +
      'einer sehr schwierigen Lage. Die häusliche Versorgung ist stark belastet ' +
      'oder gefährdet, weil der Hilfebedarf deutlich gestiegen ist und die ' +
      'bisherigen Möglichkeiten kaum noch ausreichen.',
  },
];

// --------------------------------------------------------------------------
// Template-Engine: ersetzt {ang_nom} etc. mit den konkreten Werten.
// Doppelte Leerzeichen vorm Punkt / Komma werden anschließend bereinigt.
// --------------------------------------------------------------------------
export function fuelleTemplate(template, kunde, modus = 'erwachsen') {
  const p = bauePlatzhalter(kunde, modus);
  let text = template
    .replaceAll('{ang_nom}', p.ang_nom)
    .replaceAll('{ang_akk}', p.ang_akk)
    .replaceAll('{ang_dat}', p.ang_dat)
    .replaceAll('{pat_nom_pp}', p.pat_nom_pp)
    .replaceAll('{pat_akk_pp}', p.pat_akk_pp)
    .replaceAll('{pat_dat_pp}', p.pat_dat_pp);
  // Optional: Satzanfang gross schreiben (z.B. wenn ein Template mit
  // {ang_nom} startet und der Wert kleinbuchstabig ist).
  text = text.replace(/(^|[.!?]\s+)([a-zäöüß])/g, (_, pre, c) => pre + c.toUpperCase());
  return text;
}
